import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useQuery } from "@tanstack/react-query";
import { myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions } from "../../api/registrations";
import { OwnControllerLabel, TenantsSelect, asTenant } from "../../shared/TenantsSelect";
import { useCurrentCustomer } from "../../root/CurrentOrganisationProvider";
import type { TenantOptionData } from "../../shared/TenantsSelect";
import type { ConnectedTenant } from "../../api/registrations.types";
import { flattenTenantHierarchy } from "../../shared/TenantSelect";
import { usePermission } from "../../shared/permissions";
import { useRegistrationVariant } from "./RegistrationVariantProvider";

type CreatableTenantsSelectProps = Pick<
  Parameters<typeof TenantsSelect>[0],
  "id" | "selectedTenants" | "onChange" | "error" | "inputRef" | "onBlur"
>;

export const useCreatableTenants = () => {
  const variant = useRegistrationVariant();
  const { hasPermission } = usePermission();
  const tenantHierarchyQuery = useQuery(myTenantsHierarchyWithCreateRegistrationPermissionQueryOptions());
  const tenantHierarchy = tenantHierarchyQuery.data;

  const canCreateIn = (tenant: Pick<TenantOptionData, "permissions">) =>
    hasPermission("nova-forms-api:registration:create", tenant.permissions);

  const tenants = tenantHierarchy ? flattenTenantHierarchy(tenantHierarchy) : [];

  const creatableTenants: Array<ConnectedTenant> = tenants.filter(canCreateIn).map(asTenant);

  const onlyTenant = tenants.length === 1 ? creatableTenants.at(0) : undefined;

  const takesOneUnit = variant === "Processor" || tenantHierarchy?.children.length === 0;

  return { tenantHierarchy, canCreateIn, creatableTenants, onlyTenant, takesOneUnit };
};

export const CreatableTenantsSelect: FC<CreatableTenantsSelectProps> = props => {
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const { tenantHierarchy, canCreateIn, takesOneUnit } = useCreatableTenants();

  return (
    <TenantsSelect
      {...props}
      tenantHierarchy={tenantHierarchy}
      renderOptionEndAdornment={option =>
        currentCustomer.customerKind !== "enterprise" &&
        variant === "Controller" &&
        option.isPersonalDataController && (
          <OwnControllerLabel>{t("RegistrationDrawer.Connected.Controller.Label")}</OwnControllerLabel>
        )
      }
      getChipSecondaryLabel={tenant =>
        currentCustomer.customerKind !== "enterprise" && variant === "Controller"
          ? tenant.personalDataController?.name
          : undefined
      }
      disabledTenant={tenant => !canCreateIn(tenant)}
      disabledTooltip={t("CreateRegistration.Dialog.MissingPermissionsOnTenantTooltip")}
      singleSelect={takesOneUnit}
      placeholder={t(takesOneUnit ? "TenantSelect.Placeholder" : "TenantSelect.Placeholder.Several")}
      inputState="editing"
    />
  );
};
